import React, { useState } from "react";
import { ExclamationTriangleIcon, XMarkIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { groupIssues } from "../utils/excelFormatDetector";
import { canWriteInPlace, WORKBOOK_REWRITE_NOTE } from "../utils/excelWriteBack";

const FIX_LABEL = {
  settings: { text: "Settings", className: "bg-blue-100 text-blue-800" },
  file: { text: "Excel file", className: "bg-amber-100 text-amber-800" },
  manual: { text: "Manual", className: "bg-gray-100 text-gray-700" },
};

/**
 * Shows what does not match between the uploaded sheet and Settings, and offers
 * to correct it. Purely optional - the sheet can be used, or the panel dismissed,
 * without fixing anything.
 */
const FormatFixPanel = ({ report, fileName, hasFileHandle, isFixing, onFix, onDismiss }) => {
  const [expanded, setExpanded] = useState(false);

  if (!report || !report.issues || report.issues.length === 0) return null;

  const grouped = groupIssues(report.issues);
  const writesFile = grouped.file.length > 0;
  const loss = writesFile ? WORKBOOK_REWRITE_NOTE : null;
  const canOverwrite = canWriteInPlace() && hasFileHandle;

  return (
    <div className="bg-white border-2 border-amber-300 rounded-lg shadow-md mb-6">
      <div className="px-6 py-4 border-b border-amber-200 bg-amber-50 rounded-t-lg flex items-start justify-between">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              This sheet does not match your Settings
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {report.issues.length} issue{report.issues.length === 1 ? "" : "s"} found in{" "}
              {fileName || "the uploaded file"}. Fixing is optional.
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          title="Dismiss"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="px-6 py-4">
        {report.detected && (
          <p className="text-sm text-gray-700 mb-3">
            Detected column headers on row{" "}
            <span className="font-medium">{report.detected.headerRow}</span>, so data starts on row{" "}
            <span className="font-medium">{report.detected.startRow}</span>.
          </p>
        )}

        <ul className="space-y-2">
          {report.issues.map((issue) => (
            <li key={issue.id} className="border border-gray-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{issue.title}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    FIX_LABEL[issue.fix].className
                  }`}
                >
                  {FIX_LABEL[issue.fix].text}
                </span>
              </div>
              {expanded && (
                <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap font-sans">
                  {issue.detail}
                </pre>
              )}
            </li>
          ))}
        </ul>

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>

        {writesFile && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
            <p className="font-medium">
              {grouped.file.length} of these need the Excel file itself to be changed.
            </p>
            {loss && <p className="mt-1">{loss}</p>}
            <p className="mt-1">
              {canOverwrite
                ? `${fileName} will be overwritten once you allow it.`
                : "This browser cannot write to the original file, so a fixed copy will be downloaded instead. To overwrite in place, re-open the file with the Choose file button in Chrome or Edge."}
            </p>
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={onFix}
            disabled={isFixing || !report.canFix}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <WrenchScrewdriverIcon className="h-4 w-4 mr-2" />
            {isFixing ? "Fixing..." : "Fix Format"}
          </button>
          <button
            onClick={onDismiss}
            disabled={isFixing}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Leave as is
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormatFixPanel;
